// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import ArrowIcon from '@/views/components/common/ArrowIcon';
import SimpleTable, { SimpleTableRows } from '../../../components/tables/SimpleTable';

// Utils
import { formatMaxTarget } from '../../../../utils/risk-profiles';
import { formatLocalizedString, formatMessage, formatMessageWithLocale, formatPercentage } from '../../../../utils/i18n';

// Types
import { RiskProfileRule, RiskProfileRuleData, RiskProfileRuleType } from '../../../../types/risk-profiles';
import ComparisonItem from '@/types/comparison-item';
import Dotdotdot from 'react-dotdotdot';

interface Props {
  rulesData: {
    rule: RiskProfileRule;
    data: RiskProfileRuleData[];
  }[];
  item?: ComparisonItem;
}

const formatAdjustment = (adjustment?: number) => {
  if (!adjustment) {
    return null;
  }

  if (adjustment > 0) {
    return (
      <span className="adjustment">
        <ArrowIcon margin="0 4px 0 0" title={formatMessage('Common.Upside')} type="full-up" useHTMLEntity />
        {formatPercentage(adjustment, 0)}
      </span>
    );
  }

  if (adjustment < 0) {
    return (
      <span className="adjustment">
        <ArrowIcon margin="0 4px 0 0" title={formatMessage('Common.Downside')} type="full-down" useHTMLEntity />
        {formatPercentage(Math.abs(adjustment), 0)}
      </span>
    );
  }

  return null;
};

const RiskProfileVisualizer = (props: Props) => {
  const { rulesData, item } = props;

  const rows: SimpleTableRows = rulesData.map(data => {
    if (data.rule.ruleType === RiskProfileRuleType.AllowedRisk) {
      return {
        elements: [
          formatMessageWithLocale(`RiskRating.${data.rule.value.riskLevel}`),
          formatMaxTarget(data.rule.value.max, 0),
          data.data[0].value ? formatPercentage(data.data[0].value, 0) : formatPercentage(0, 0),
          formatAdjustment(data.data[0].adjustment),
        ],
      };
    }
    return { elements: [] };
  });

  const headers: React.ReactNode[] = ['', formatMessageWithLocale('Common.Target')];
  headers.push(
    <Dotdotdot useNativeClamp clamp={2}>
      {item != null ? formatLocalizedString(item.name) : formatMessageWithLocale('RiskProfile.AllItems')}
    </Dotdotdot>,
  );
  headers.push('');

  const tableData = {
    headers,
    rows,
  };

  return (
    <div className={classNames('risk-profile-visualizer')}>
      <SimpleTable data={tableData} />
    </div>
  );
};

export default RiskProfileVisualizer;
